/**
 * Wizard 逐步验证工具 - AI Create 和 Community Idea 模态框共用。
 * blur 时即校验并内联显示错误；Continue / Submit 按钮做最终全量校验。
 * 模块对外只暴露 showFieldError / clearFieldError / clearPanelErrors 三个函数，
 * 调用方不感知内部 DOM 操作细节。
 */
import { byId } from "./domUtils";
import type { Root } from "./prototypeTypes";
import { AI_CHAPTER_TARGET_MAX, AI_CHAPTER_TARGET_MIN, aiChapterPresetBounds } from '../../domain/aiChapterTargetPolicy';

const ERROR_CLASS = "wizard-field-error";

/**
 * 在指定输入框（#fieldId）的父容器末尾插入/更新一条红色错误文案。
 * 如果该字段已有错误提示，则更新文案而非叠加。
 */
export function showFieldError(root: Root, fieldId: string, message: string): void {
	const field = byId(root, fieldId);
	if (!field) return;
	const container = field.parentElement;
	if (!container) return;
	let errorEl = container.querySelector<HTMLParagraphElement>(`.${ERROR_CLASS}`);
	if (!errorEl) {
		errorEl = document.createElement("p");
		errorEl.className = ERROR_CLASS;
		errorEl.id = `${fieldId}-error`;
		errorEl.setAttribute('role', 'alert');
		errorEl.setAttribute('aria-live', 'polite');
		container.appendChild(errorEl);
	}
	errorEl.textContent = message;
	// 给输入框加错误边框
	field.classList.add("input-error");
	field.setAttribute('aria-invalid', 'true');
	field.setAttribute('aria-errormessage', errorEl.id);
	const descriptions = new Set((field.getAttribute('aria-describedby') ?? '').split(/\s+/).filter(Boolean));
	descriptions.add(errorEl.id);
	field.setAttribute('aria-describedby', Array.from(descriptions).join(' '));
}

/**
 * 清除指定输入框的内联错误（如果存在）。
 */
export function clearFieldError(root: Root, fieldId: string): void {
	const field = byId(root, fieldId);
	if (!field) return;
	const container = field.parentElement;
	if (!container) return;
	const errorEl = container.querySelector(`.${ERROR_CLASS}`);
	if (errorEl) errorEl.remove();
	field.classList.remove("input-error");
	field.removeAttribute('aria-invalid');
	field.removeAttribute('aria-errormessage');
	const errorId = `${fieldId}-error`;
	const descriptions = (field.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((id) => id && id !== errorId);
	if (descriptions.length > 0) field.setAttribute('aria-describedby', descriptions.join(' '));
	else field.removeAttribute('aria-describedby');
}

/** Move keyboard focus to the first invalid field after an explicit submit/continue action. */
export function focusFirstFieldError(root: Root, fieldIds: string[]): void {
	for (const fieldId of fieldIds) {
		const field = byId(root, fieldId);
		if (!field?.classList.contains('input-error')) continue;
		const target = field.matches('input,textarea,select,button,a[href],[tabindex]')
			? field
			: field.querySelector<HTMLElement>('input:not([disabled]),textarea:not([disabled]),select:not([disabled]),button:not([disabled]),a[href],[tabindex]:not([tabindex="-1"])');
		if (!target) continue;
		target.focus({ preventScroll: true });
		target.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
		return;
	}
}

/**
 * 清除某个 wizard panel 内所有字段的内联错误。
 * 用于 Back 导航或面板切换时重置错误状态。
 */
export function clearPanelErrors(root: Root, panelId: string): void {
	const panel = byId(root, panelId);
	if (!panel) return;
	panel.querySelectorAll(`.${ERROR_CLASS}`).forEach((el) => el.remove());
	panel.querySelectorAll<HTMLElement>(".input-error").forEach((el) => {
		el.classList.remove("input-error");
		el.removeAttribute('aria-invalid');
		el.removeAttribute('aria-errormessage');
		const descriptions = (el.getAttribute('aria-describedby') ?? '').split(/\s+/).filter((id) => id && id !== `${el.id}-error`);
		if (descriptions.length > 0) el.setAttribute('aria-describedby', descriptions.join(' '));
		else el.removeAttribute('aria-describedby');
	});
}

/**
 * Validate an optional subtitle field. Subtitle is optional, but if the
 * user fills it in, it must meet the same minimum length as the title (≥8 chars).
 * Returns an error message string, or null if valid.
 */
export function validateSubtitle(value: string): string | null {
	if (value.length === 0) return null;
	if (value.length < 8) return "Subtitle must be at least 8 characters if provided.";
	if (value.length > 120) return "Subtitle must not exceed 120 characters.";
	return null;
}

/** Community and AI Create use one optional subtitle contract. */
export function validateIdeaSubtitle(value: string): string | null {
	return validateSubtitle(value);
}

/**
 * Validate the hidden chapter-count carrier. The UI only ever writes one of
 * the presets here (Auto-Plan / 1-5 / 6-8 / 9-12 / 13-15), so this is a
 * defensive 1..15 check; the server still enforces the affordable lower bound.
 * Returns an error message string, or null if valid/empty.
 */
export function validateChapterCount(value: string): string | null {
	const trimmed = value.trim();
	if (trimmed.length === 0) return null;
	if (aiChapterPresetBounds(trimmed)) return null;
	// Single integer: "5"
	if (/^\d+$/.test(trimmed)) {
		const n = Number(trimmed);
		return n >= AI_CHAPTER_TARGET_MIN && n <= AI_CHAPTER_TARGET_MAX
			? null
			: `Chapter count must be between ${AI_CHAPTER_TARGET_MIN} and ${AI_CHAPTER_TARGET_MAX}.`;
	}
	// Range: "3-8"
	const rangeMatch = trimmed.match(/^(\d+)\s*-\s*(\d+)$/);
	if (rangeMatch) {
		const min = Number(rangeMatch[1]);
		const max = Number(rangeMatch[2]);
		if (min > max) return `Range start (${min}) must not exceed end (${max}).`;
		return min >= AI_CHAPTER_TARGET_MIN && max <= AI_CHAPTER_TARGET_MAX
			? null
			: `Chapter range must be within ${AI_CHAPTER_TARGET_MIN}-${AI_CHAPTER_TARGET_MAX}.`;
	}
	return `Chapter count must be a preset (Auto-Plan, 1-5, 6-8, 9-12 or 13-15).`;
}

/**
 * Validate an optional long-text field (strengths, weaknesses, etc.).
 * If the user fills it in, it must be 2-300 characters.
 * Returns an error message string, or null if valid/empty.
 */
const OPT_LONG_MIN = 2;
const OPT_LONG_MAX = 300;

export function validateOptionalLongText(value: string, fieldName: string): string | null {
	const trimmed = value.trim();
	if (trimmed.length === 0) return null;
	if (trimmed.length < OPT_LONG_MIN) return `${fieldName} must be at least ${OPT_LONG_MIN} characters if provided.`;
	if (trimmed.length > OPT_LONG_MAX) return `${fieldName} must not exceed ${OPT_LONG_MAX} characters.`;
	return null;
}

/**
 * Field-level blur validation map: when a field loses focus, run its
 * validation rule. If the value passes (null), clear any existing error.
 * If it fails (non-null message), show the inline error.
 *
 * This fires on every blur -- not just fields that already have errors --
 * so the user gets immediate feedback when they leave a field.
 * The Continue / Submit buttons do a final full-step validation pass.
 */
const FIELD_VALIDATORS: Record<string, (value: string) => string | null> = {
	// Required fields: empty or too short both produce a concrete message.
	aiTopicTitle: (v) => {
		const trimmed = v.trim();
		if (trimmed.length === 0) return "Please enter a topic title with at least 8 characters.";
		if (trimmed.length < 8) return "Topic title must be at least 8 characters.";
		if (trimmed.length > 120) return "Topic title must not exceed 120 characters.";
		return null;
	},
	ideaTitle: (v) => {
		const trimmed = v.trim();
		if (trimmed.length === 0) return "Please enter a topic title with at least 8 characters.";
		if (trimmed.length < 8) return "Topic title must be at least 8 characters.";
		if (trimmed.length > 120) return "Topic title must not exceed 120 characters.";
		return null;
	},
	aiTopicSubtitle: validateSubtitle,
	ideaSubtitle: validateIdeaSubtitle,
	// Optional fields
	aiTopicChapters: validateChapterCount,
	aiTopicStrengths: (v) => validateOptionalLongText(v, "Expected Strengths"),
	aiTopicWeaknesses: (v) => validateOptionalLongText(v, "What to Spend Less Time On"),
	ideaStrengths: (v) => validateOptionalLongText(v, "Expected Strengths"),
	ideaWeaknesses: (v) => validateOptionalLongText(v, "Areas to Go Deeper"),
	// Feedback form fields
	feedbackSubject: (v) => {
		const trimmed = v.trim();
		if (trimmed.length === 0) return "Please enter a subject with at least 3 characters.";
		if (trimmed.length < 3) return "Subject must be at least 3 characters.";
		return null;
	},
	feedbackBody: (v) => {
		const trimmed = v.trim();
		if (trimmed.length === 0) return "Please describe your feedback with at least 10 characters.";
		if (trimmed.length < 10) return "Details must be at least 10 characters.";
		return null;
	},
	feedbackReplyBody: (v) => {
		const trimmed = v.trim();
		if (trimmed.length === 0) return "Please enter your reply with at least 2 characters.";
		if (trimmed.length < 2) return "Reply must be at least 2 characters.";
		return null;
	},
};

/**
 * Attach a delegated `focusout` listener on root that validates each
 * field on blur. If the value passes, clear any existing error; if it
 * fails, show the inline error message. Call once during controller
 * mount; returns a cleanup function for unmount.
 */
export function attachInlineErrorClearer(root: Root): () => void {
	const onFocusOut = (event: Event) => {
		const target = event.target as HTMLElement | null;
		if (!target) return;
		const fieldId = target.id;
		if (!fieldId) return;
		const validator = FIELD_VALIDATORS[fieldId];
		if (!validator) return;
		const value = (target as HTMLInputElement | HTMLTextAreaElement).value ?? "";
		const result = validator(value);
		if (result === null) {
			clearFieldError(root, fieldId);
		} else {
			showFieldError(root, fieldId, result);
		}
	};
	root.addEventListener("focusout", onFocusOut);
	return () => root.removeEventListener("focusout", onFocusOut);
}
